import { motion } from "framer-motion";
import { Link, useParams } from "react-router-dom";
import { FuelMenuNav } from "@/components/fuel-activity/FuelMenuNav";
import { UserFuelActivityAnalytics } from "@/components/fuel-activity/UserFuelActivityAnalytics";
import { PageHeader } from "@/components/ui/PageHeader";
import { ExplorerSkeleton } from "@/components/ui/PageSkeleton";
import { SafeImage } from "@/components/ui/SafeImage";
import { defaultFuelActivityFilters } from "@/lib/fuel-analytics";
import { useUserFuelActivity } from "@/lib/queries";
import { imageSrc, userDisplayName } from "@/lib/utils";
import type { FuelActivityFilters } from "@/types/database";
import { useState } from "react";

const pageVariants = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -4 },
};

export function UserFuelActivityPage() {
  const { userId = "" } = useParams();
  const { data, isLoading, error } = useUserFuelActivity(userId);
  const [filters, setFilters] = useState<FuelActivityFilters>(defaultFuelActivityFilters());

  const user = data?.user ?? null;
  const name = user ? userDisplayName(user) : userId.slice(0, 8);
  const profilePhoto = user ? imageSrc(user.profilePhoto) : null;

  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
      className="space-y-6"
    >
      <Link
        to="/fuel-activity"
        className="inline-flex text-sm font-semibold text-muted transition hover:text-foreground"
      >
        ← User fuel activity
      </Link>

      <PageHeader
        title={name}
        description={user?.email ?? "Fuel consumption and top-up breakdown for this user."}
        showGreeting={false}
        action={
          profilePhoto ? (
            <SafeImage
              src={profilePhoto}
              alt={name}
              width={48}
              height={48}
              className="h-12 w-12 rounded-full object-cover"
            />
          ) : (
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-accent-subtle text-sm font-bold text-accent-foreground">
              {name.charAt(0).toUpperCase()}
            </div>
          )
        }
      />

      <FuelMenuNav />

      {isLoading ? (
        <ExplorerSkeleton title="User fuel breakdown" />
      ) : error || !data ? (
        <p className="text-sm text-red-600">Failed to load this user&apos;s fuel activity.</p>
      ) : (
        <UserFuelActivityAnalytics
          user={user}
          userID={userId}
          logs={data.logs}
          cars={data.cars}
          stations={data.stations}
          filters={filters}
          onFiltersChange={setFilters}
        />
      )}
    </motion.div>
  );
}
